import InfoIcon from "@/assets/colorable/info.svg?react";
import Button from "@/components/Common/Button";
import MarkdownEditor from "@/components/Common/MarkdownEditor";
import {Tooltip} from "@/components/Common/Tooltip";
import type {ContentSubmissionListSerializer} from "@/generated/DjangoTypes";
import {useManagementCampaign} from "@/store/campaignManagement";
import {revisionAtom} from "@/store/jotaiAtoms";
import {CAPTION_LIMIT} from "@/utils/campaignUtils";
import {useAtom} from "jotai";
import {useId, type FC} from "react";
import {useTranslation} from "react-i18next";

const RevisionForm: FC<{id: number; type: ContentSubmissionListSerializer["media_type"]}> = ({id, type}) => {
  const {t} = useTranslation(["content_approval", "common"]);
  const campaign = useManagementCampaign();
  const [revision, setRevision] = useAtom(revisionAtom);
  const revisionInfoId = useId();
  const revisionInfos: string[] = [t("revision_info_one"), t("revision_info_two")];

  const addCampaignTags = ({tagType}: {tagType: "account_tag" | "hashtag"}) => {
    const prefix = tagType === "hashtag" ? "#" : "@";
    const tags = tagType === "hashtag" ? campaign.hashtags : campaign.account_tags;

    if (!tags) {
      return;
    }

    const formattedTags = tags
      .split(":")
      .filter(tag => !!tag)
      .map(tag => `${prefix}${tag}`)
      .join(" ");
    setRevision(state => {
      state[id].caption = state[id].caption ? `${state[id].caption}\n${formattedTags}` : formattedTags;
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <span className="text-neutral-black text-xs">{t("content_revisions", "Content revisions")}</span>
          <MarkdownEditor
            autoSize
            className="md-editor-text-xs border-neutral-white-dark divide-neutral-white-dark max-h-100 min-h-34 border shadow-none"
            placeholder={t("type_revisions_placeholder", {type: t(type === "VIDEO" ? "common:video" : "common:image")})}
            onChange={value =>
              setRevision(state => {
                if (!state[id]) {
                  state[id] = {caption: "", feedback: ""};
                }

                state[id].feedback = value ?? "";
              })
            }
            value={revision[id]?.feedback ?? ""}
            visibleDragbar={false}
          />
        </div>
        <div className="relative flex flex-col gap-1">
          <span className="text-neutral-black text-xs">{t("caption_revisions")}</span>
          <div className="relative">
            <textarea
              className="border-neutral-white-dark focus:ring-primary max-h-100 min-h-34 w-full resize-y rounded border p-2 pb-7 text-xs outline-none focus:ring-1"
              style={{height: "136px"}}
              maxLength={CAPTION_LIMIT}
              onChange={e =>
                setRevision(state => {
                  if (!state[id]) {
                    state[id] = {caption: "", feedback: ""};
                  }

                  state[id].caption = e.target.value;
                })
              }
              value={revision[id]?.caption ?? ""}
            />
            <div className="absolute bottom-2 left-2 flex items-center gap-2">
              <InfoIcon
                className="text-primary-dark-solid size-3.5 shrink-0 cursor-pointer outline-none"
                data-tooltip-id={`revision-info-${revisionInfoId}`}
              />
              <span className="text-neutral-grey-dark text-xs">{t("caption_revision_info")}</span>
              <Tooltip
                id={`revision-info-${revisionInfoId}`}
                clickable
                place="bottom"
                variant="light"
                opacity={1}
                delayShow={200}
                className="z-100 flex max-w-51.5 flex-col gap-1 p-3 shadow-md"
                classNameArrow="shadow-md">
                <div className="flex items-center gap-2">
                  <InfoIcon className="text-primary-dark-solid size-3.5 shrink-0" />
                  <span className="text-neutral-black text-sm font-semibold">{t("caption_revision_info")}</span>
                </div>
                <span className="text-neutral-grey-dark text-2xs leading-3.5">{t("caption_revision_detail")}</span>
              </Tooltip>
            </div>
            <span className="text-neutral-grey-dark text-2xs absolute right-2 bottom-2 leading-3.5">
              {revision[id]?.caption?.length ?? 0} / {CAPTION_LIMIT}
            </span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <Button
              onClick={() => addCampaignTags({tagType: "hashtag"})}
              isDisabled={!campaign.hashtags}
              buttonStyle="White"
              className="h-12 basis-1/2 rounded-full text-sm">
              #&nbsp;&nbsp;{t("add_hashtags")}
            </Button>
            <Button
              onClick={() => addCampaignTags({tagType: "account_tag"})}
              isDisabled={!campaign.account_tags}
              buttonStyle="White"
              className="h-12 basis-1/2 rounded-full text-sm">
              @&nbsp;&nbsp;{t("add_tags")}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        {revisionInfos.map((info, index) => (
          <div key={index} className="flex items-center gap-2">
            <InfoIcon className="text-neutral-grey-dark size-3.5 shrink-0" />
            <span className="text-neutral-grey-dark text-xs">{info}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RevisionForm;
